import { Injectable } from '@angular/core';
import { ValidationResult } from '../interfaces/question.interface';

@Injectable({
  providedIn: 'root'
})
export class ValidationService {

  constructor() { }

  validateCompanyName(value: string): ValidationResult {
    if (!value.trim()) {
      return { valid: false, message: 'Company name is required.' };
    }
    if (value.trim().length < 2) {
      return { valid: false, message: 'Company name must be at least 2 characters long.' };
    }
    if (value.trim().length > 100) {
      return { valid: false, message: 'Company name must be less than 100 characters.' };
    }
    return { valid: true, value: value.trim() };
  }

  validatePhoneNumber(value: string): ValidationResult {
    if (!value.trim()) {
      return { valid: false, message: 'Phone number is required.' };
    }
    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
    const cleanPhone = value.replace(/[\s\-\(\)\.]/g, '');
    if (!phoneRegex.test(cleanPhone) && cleanPhone.length < 10) {
      return { valid: false, message: 'Please enter a valid phone number.' };
    }
    return { valid: true, value: value.trim() };
  }

  validateCity(value: string): ValidationResult {
    if (!value.trim()) {
      return { valid: false, message: 'City is required.' };
    }
    if (value.trim().length < 2) {
      return { valid: false, message: 'City must be at least 2 characters long.' };
    }
    return { valid: true, value: value.trim() };
  }

  validateCountryRegion(value: string): ValidationResult {
    if (!value.trim()) {
      return { valid: false, message: 'Country/Region is required.' };
    }
    if (value.trim().length < 2) {
      return { valid: false, message: 'Country/Region must be at least 2 characters long.' };
    }
    return { valid: true, value: value.trim() };
  }

  validateWebsiteURL(value: string): ValidationResult {
    if (!value.trim()) {
      return { valid: true, value: '' };
    }
    try {
      new URL(value.trim());
      return { valid: true, value: value.trim() };
    } catch {
      return { valid: false, message: 'Please enter a valid URL (e.g., https://www.example.com)' };
    }
  }

  validateStreetAddress(value: string): ValidationResult {
    if (!value.trim()) {
      return { valid: false, message: 'Street address is required.' };
    }
    
    const address = value.trim();
    // Check if the address contains at least a number and some text
    const hasNumber = /\d/.test(address);
    const hasText = /[a-zA-Z]/.test(address);
    
    if (!hasNumber || !hasText) {
      return { valid: false, message: 'Please enter a valid street address with both number and street name.' };
    }
    
    return { valid: true, value: address };
  }

  validateStreetAddress2(value: string): ValidationResult {
    return { valid: true, value: value.trim() };
  }

  validateLogoURL(value: string): ValidationResult {
    if (!value.trim()) {
      return { valid: true, value: '' };
    }
    try {
      new URL(value.trim());
      return { valid: true, value: value.trim() };
    } catch {
      return { valid: false, message: 'Please enter a valid image URL' };
    }
  }

  validateContactEmail(value: string): ValidationResult {
    if (!value.trim()) {
      return { valid: false, message: 'Contact email is required.' };
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value.trim())) {
      return { valid: false, message: 'Please enter a valid email address.' };
    }
    return { valid: true, value: value.trim() };
  }

  validatePostalCode(value: string): ValidationResult {
    if (!value.trim()) {
      return { valid: false, message: 'Postal code is required.' };
    }
    if (value.trim().length < 3) {
      return { valid: false, message: 'Please enter a valid postal code.' };
    }
    return { valid: true, value: value.trim() };
  }

  validateAboutContent(value: string): ValidationResult {
    if (!value.trim()) {
      return { valid: false, message: 'About content is required.' };
    }
    if (value.trim().length < 20) {
      return { valid: false, message: 'Please provide at least 20 characters describing your business.' };
    }
    if (value.trim().length > 1000) {
      return { valid: false, message: 'About content must be less than 1000 characters.' };
    }
    return { valid: true, value: value.trim() };
  }

  validateLogoFile(value: File): ValidationResult {
    if (!value) {
      return { valid: false, message: 'Please select a file.' };
    }

    if (!(value instanceof File)) {
      return { valid: false, message: 'Please select a valid file.' };
    }

    // Check file type
    const allowedTypes = ['image/jpeg', 'image/png'];
    if (!allowedTypes.includes(value.type)) {
      return { valid: false, message: 'Please upload a JPEG or PNG image file.' };
    }

    // Check file size (5MB max)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (value.size > maxSize) {
      return { valid: false, message: 'File size must be less than 5MB.' };
    }

    // Check if file name is reasonable
    if (value.name.length > 255) {
      return { valid: false, message: 'File name is too long.' };
    }

    return { valid: true, value: value.name };
  }

  validateEquipment(value: string): ValidationResult {
    if (!value.trim()) {
      return { valid: false, message: 'Equipment information is required.' };
    }
    if (value.trim().length < 3) {
      return { valid: false, message: 'Please provide at least 3 characters for equipment details.' };
    }
    if (value.trim().length > 1000) {
      return { valid: false, message: 'Equipment information must be less than 1000 characters.' };
    }
    return { valid: true, value: value.trim() };
  }

  validateCapabilities(value: string | string[]): ValidationResult {
    let capabilities: string[];
    
    // Handle both string and array inputs
    if (typeof value === 'string') {
      capabilities = value ? value.split(',').map(s => s.trim()).filter(s => s.length > 0) : [];
    } else if (Array.isArray(value)) {
      capabilities = value.filter(s => s && s.trim().length > 0);
    } else {
      capabilities = [];
    }
    
    if (capabilities.length === 0) {
      return { valid: false, message: 'Please select at least one capability.' };
    }
    if (capabilities.length > 25) {
      return { valid: false, message: 'Please select no more than 25 capabilities.' };
    }
    
    // Return as comma-separated string for storage
    return { valid: true, value: capabilities.join(', ') };
  }

  validateColorTheme(value: string): ValidationResult {
    // List of valid color themes (these should match the keys in COLOR_THEMES)
    const validThemes = ['default', 'red', 'green', 'purple', 'orange', 'teal', 'dark', 'warm', 'cool'];
    
    if (!value || !value.trim()) {
      // Default to 'default' theme if no selection
      return { valid: true, value: 'default' };
    }
    
    const theme = value.trim();
    if (!validThemes.includes(theme)) {
      return { valid: false, message: 'Please select a valid color theme.' };
    }
    
    return { valid: true, value: theme };
  }
} 